import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { imgUrl } from './api'

export const money = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n) || 0)

export const dateFmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'

export const stars = (n) => '★'.repeat(n) + '☆'.repeat(Math.max(0, 5 - n))

/* ---------- toasts + confirm dialog ---------- */
const Ctx = createContext(null)
export const useAdmin = () => useContext(Ctx)

export function AdminProviders({ children }) {
  const [toasts, setToasts] = useState([])
  const [ask, setAsk] = useState(null)

  const toast = useCallback((message, type = 'ok') => {
    const id = Date.now() + Math.random()
    setToasts((t) => [...t, { id, message, type }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500)
  }, [])

  const confirm = useCallback((opts) => new Promise((resolve) => setAsk({ ...opts, resolve })), [])
  const answer = (v) => {
    ask.resolve(v)
    setAsk(null)
  }

  return (
    <Ctx.Provider value={{ toast, confirm }}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.type}`}>{t.message}</div>
        ))}
      </div>
      {ask && (
        <Modal
          small
          title={ask.title}
          onClose={() => answer(false)}
          footer={
            <>
              <button className="btn" onClick={() => answer(false)}>Cancel</button>
              <button className="btn danger" onClick={() => answer(true)}>{ask.confirmText || 'Delete'}</button>
            </>
          }
        >
          <p>{ask.body}</p>
        </Modal>
      )}
    </Ctx.Provider>
  )
}

/* ---------- data loading ---------- */
export function useAsync(fn, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null })
  const [tick, setTick] = useState(0)
  useEffect(() => {
    let live = true
    setState((s) => ({ ...s, loading: true, error: null }))
    fn()
      .then((data) => live && setState({ data, loading: false, error: null }))
      .catch((error) => live && setState({ data: null, loading: false, error }))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])
  return { ...state, reload: () => setTick((t) => t + 1) }
}

/* ---------- building blocks ---------- */
export function Modal({ title, onClose, children, footer, small, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${small ? ' small' : ''}${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  )
}

export function PageHead({ title, sub, actions }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {sub && <p className="muted">{sub}</p>}
      </div>
      <div className="page-actions">{actions}</div>
    </div>
  )
}

export function Field({ label, error, hint, children }) {
  return (
    <label className={`field${error ? ' has-error' : ''}`}>
      <span className="field-label">{label}</span>
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="err">{error}</span>}
    </label>
  )
}

export function ImageInput({ current, file, onChange }) {
  const [preview, setPreview] = useState(null)
  useEffect(() => {
    if (!file) return setPreview(null)
    const u = URL.createObjectURL(file)
    setPreview(u)
    return () => URL.revokeObjectURL(u)
  }, [file])
  const src = preview || imgUrl(current)
  return (
    <div className="imgin">
      <div className="imgin-box">{src ? <img src={src} alt="" /> : <span>No image</span>}</div>
      <label className="btn">
        {src ? 'Change image' : 'Choose image'}
        <input type="file" accept="image/*" hidden onChange={(e) => onChange(e.target.files[0] || null)} />
      </label>
    </div>
  )
}

export function Pager({ page, total, per, onPage }) {
  const pages = Math.max(1, Math.ceil(total / per))
  if (total <= per) return null
  return (
    <div className="pager">
      <span className="muted">
        {(page - 1) * per + 1}-{Math.min(page * per, total)} of {total}
      </span>
      <button className="btn sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <button className="btn sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  )
}

export function Loading() {
  return <div className="state">Loading…</div>
}

export function Empty({ title, body, action }) {
  return (
    <div className="state">
      <strong>{title}</strong>
      {body && <p className="muted">{body}</p>}
      {action}
    </div>
  )
}

export function ErrorState({ error, endpoint, onRetry }) {
  let msg = error?.message || 'Something went wrong.'
  if (endpoint && error?.status === 404) msg = `The server has no ${endpoint} route yet. Add it to your backend, then retry.`
  if (error?.status === 401 || error?.status === 403) msg = 'Your session has expired or this account is not allowed to do that. Sign in again.'
  return (
    <div className="state error">
      <strong>Could not load this page</strong>
      <p>{msg}</p>
      {onRetry && <button className="btn" onClick={onRetry}>Try again</button>}
    </div>
  )
}

export function Status({ value }) {
  const v = String(value || '').toLowerCase()
  const tone = { delivered: 'ok', paid: 'ok', shipped: 'info', processing: 'warn', pending: 'warn', cancelled: 'danger', failed: 'danger' }[v] || 'neutral'
  return <span className={`badge ${tone}`}>{value || '-'}</span>
}
